import type { KrajinaSkupina, Zapas, Krajina } from "@/types/database";

export interface VysledokVzajomnychZapasov {
  body: number;
  skore: {
    za: number;
    priem: number;
  };
  vyhry: number;
  prehry: number;
}

export interface VzajomneZapasy {
  protiTimom: Record<string, VysledokVzajomnychZapasov>;
  body: number;
  skore: {
    za: number;
    priem: number;
  };
  rozdiel: number;
}

/**
 * Vypočíta výsledky vo vzájomných zápasoch pre všetky tímy v skupine
 * Podľa IIHF pravidiel: body, gólový rozdiel, strelené góly
 */
export function vypocitajVzajomneZapasy(
  timy: (KrajinaSkupina & { krajina: Krajina })[],
  zapasy: (Zapas & { domaci: Krajina; hosti: Krajina })[],
): Record<string, VzajomneZapasy> {
  const timIds = timy.map((t) => t.krajina.id);
  const vzajomneZapasy = zapasy.filter(
    (z) => timIds.includes(z.domaci.id) && timIds.includes(z.hosti.id),
  );

  const vysledky: Record<string, VzajomneZapasy> = {};

  // Inicializácia pre všetky tímy
  timy.forEach((tim) => {
    vysledky[tim.krajina.id] = {
      protiTimom: {},
      body: 0,
      skore: { za: 0, priem: 0 },
      rozdiel: 0,
    };
  });

  // Spracovanie vzájomných zápasov
  vzajomneZapasy.forEach((zapas) => {
    const domaciId = zapas.domaci.id;
    const hostiId = zapas.hosti.id;

    if (zapas.skore_domaci === null || zapas.skore_hosti === null) return;

    // Body pre domácich
    const bodyDomaci =
      zapas.skore_domaci > zapas.skore_hosti
        ? 3
        : zapas.skore_domaci === zapas.skore_hosti
          ? 1
          : 0;

    // Body pre hostí
    const bodyHosti =
      zapas.skore_hosti > zapas.skore_domaci
        ? 3
        : zapas.skore_hosti === zapas.skore_domaci
          ? 1
          : 0;

    // Výsledky domácich proti hosťom
    if (!vysledky[domaciId].protiTimom[hostiId]) {
      vysledky[domaciId].protiTimom[hostiId] = {
        body: 0,
        skore: { za: 0, priem: 0 },
        vyhry: 0,
        prehry: 0,
      };
    }
    vysledky[domaciId].protiTimom[hostiId].body += bodyDomaci;
    vysledky[domaciId].protiTimom[hostiId].skore.za += zapas.skore_domaci;
    vysledky[domaciId].protiTimom[hostiId].skore.priem += zapas.skore_hosti;
    vysledky[domaciId].protiTimom[hostiId].vyhry +=
      zapas.skore_domaci > zapas.skore_hosti ? 1 : 0;
    vysledky[domaciId].protiTimom[hostiId].prehry +=
      zapas.skore_domaci < zapas.skore_hosti ? 1 : 0;

    // Výsledky hostí proti domácim
    if (!vysledky[hostiId].protiTimom[domaciId]) {
      vysledky[hostiId].protiTimom[domaciId] = {
        body: 0,
        skore: { za: 0, priem: 0 },
        vyhry: 0,
        prehry: 0,
      };
    }
    vysledky[hostiId].protiTimom[domaciId].body += bodyHosti;
    vysledky[hostiId].protiTimom[domaciId].skore.za += zapas.skore_hosti;
    vysledky[hostiId].protiTimom[domaciId].skore.priem += zapas.skore_domaci;
    vysledky[hostiId].protiTimom[domaciId].vyhry +=
      zapas.skore_hosti > zapas.skore_domaci ? 1 : 0;
    vysledky[hostiId].protiTimom[domaciId].prehry +=
      zapas.skore_hosti < zapas.skore_domaci ? 1 : 0;

    // Celkové štatistiky vzájomných zápasov
    vysledky[domaciId].body += bodyDomaci;
    vysledky[domaciId].skore.za += zapas.skore_domaci;
    vysledky[domaciId].skore.priem += zapas.skore_hosti;

    vysledky[hostiId].body += bodyHosti;
    vysledky[hostiId].skore.za += zapas.skore_hosti;
    vysledky[hostiId].skore.priem += zapas.skore_domaci;
  });

  // Výpočet rozdielov
  Object.keys(vysledky).forEach((timId) => {
    vysledky[timId].rozdiel =
      vysledky[timId].skore.za - vysledky[timId].skore.priem;
  });

  return vysledky;
}

/**
 * Zoradí tímy v skupine podľa IIHF pravidiel
 * 1. Body
 * 2. Vzájomné zápasy (ak rovnaké body)
 * 3. Gólový rozdiel
 * 4. Strelené góly
 * 5. IIHF ranking
 */
export function zoradTimyPodlaIIHF(
  timy: (KrajinaSkupina & { krajina: Krajina })[],
  zapasy: (Zapas & { domaci: Krajina; hosti: Krajina })[],
): (KrajinaSkupina & { krajina: Krajina; vzajomneZapasy?: VzajomneZapasy })[] {
  const vzajomneVysledky = vypocitajVzajomneZapasy(timy, zapasy);

  const timySVysledkami = timy.map((tim) => ({
    ...tim,
    vzajomneZapasy: vzajomneVysledky[tim.krajina.id],
  }));

  return timySVysledkami.sort((a, b) => {
    // 1. Body (zostupne)
    if (b.body !== a.body) {
      return b.body - a.body;
    }

    // 2. Vzájomné zápasy medzi tímami s rovnakým počtom bodov
    if (a.vzajomneZapasy && b.vzajomneZapasy) {
      const aVsB = a.vzajomneZapasy.protiTimom[b.krajina.id];
      const bVsA = b.vzajomneZapasy.protiTimom[a.krajina.id];

      if (aVsB && bVsA) {
        // Porovnanie bodov zo vzájomných zápasov
        if (aVsB.body !== bVsA.body) {
          return bVsA.body - aVsB.body;
        }

        // Ak rovnaké body, porovnanie skóre zo vzájomných zápasov
        const aRozdiel = aVsB.skore.za - aVsB.skore.priem;
        const bRozdiel = bVsA.skore.za - bVsA.skore.priem;
        if (aRozdiel !== bRozdiel) {
          return bRozdiel - aRozdiel;
        }

        // Ak rovnaké rozdiely, porovnanie strelených gólov zo vzájomných zápasov
        if (aVsB.skore.za !== bVsA.skore.za) {
          return bVsA.skore.za - aVsB.skore.za;
        }
      }
    }

    // 3. Rozdiel gólov vo všetkých skupinových zápasoch
    const rozdielA = a.skore_za - a.skore_priem;
    const rozdielB = b.skore_za - b.skore_priem;
    if (rozdielB !== rozdielA) {
      return rozdielB - rozdielA;
    }

    // 4. Počet strelených gólov vo všetkých skupinových zápasoch
    if (b.skore_za !== a.skore_za) {
      return b.skore_za - a.skore_za;
    }

    // 5. IIHF ranking pred turnajom (nižšie číslo = lepší ranking)
    const aRanking = a.krajina.iihf_rebricek ?? 999;
    const bRanking = b.krajina.iihf_rebricek ?? 999;
    if (aRanking !== bRanking) {
      return aRanking - bRanking;
    }

    // 6. Ak všetko rovnaké, abecedné poradie
    return a.krajina.nazov.localeCompare(b.krajina.nazov);
  });
}
